import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
} from '@mui/material'
import {
  Campaign as BroadcastIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
} from '@mui/icons-material'

import BroadcastDialog from '../dialogs/BroadcastDialog'
import CreateFlowDialog from '../dialogs/CreateFlowDialog'
import ExportDialog from '../dialogs/ExportDialog'
import AddSMBDialog from '../dialogs/AddSMBDialog'
import { useNotification } from '../../contexts/NotificationContext'
import { useAuth } from '../../contexts/AuthContext'
import { SMBData } from '../../services/api'

const QuickActions: React.FC = () => {
  const { showNotification } = useNotification()
  const { user } = useAuth()
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [flowOpen, setFlowOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [addSMBOpen, setAddSMBOpen] = useState(false)

  const handleBroadcastComplete = () => {
    setBroadcastOpen(false)
    showNotification('Broadcast message sent successfully!', 'success')
  }

  const handleFlowComplete = () => {
    setFlowOpen(false)
    showNotification('Flow created successfully!', 'success')
  }

  const handleExportComplete = () => {
    setExportOpen(false)
    showNotification('Export started! You will receive an email when ready.', 'success')
  }

  const handleSMBAddSuccess = (smb: SMBData) => {
    setAddSMBOpen(false)
    showNotification(`Business "${smb.name}" added successfully!`, 'success')
  }

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" component="h3" fontWeight="bold" sx={{ mb: 2 }}>
            Quick Actions
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {user?.role === 'agency' ? (
              // Agency Quick Actions
              <>
                <Button
                  variant="contained"
                  startIcon={<BusinessIcon />}
                  onClick={() => setAddSMBOpen(true)}
                  fullWidth
                  size="large"
                  sx={{ 
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    '&:hover': { 
                      background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                    }
                  }}
                >
                  Add Business
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<BroadcastIcon />}
                  onClick={() => setBroadcastOpen(true)}
                  fullWidth
                  size="large"
                >
                  Broadcast Message
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => setExportOpen(true)}
                  fullWidth
                  size="large"
                >
                  Export Reports
                </Button>
              </>
            ) : (
              // SMB Quick Actions
              <>
                <Button
                  variant="contained"
                  startIcon={<BroadcastIcon />}
                  onClick={() => setBroadcastOpen(true)}
                  fullWidth
                  size="large"
                >
                  Broadcast Message
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setFlowOpen(true)}
                  fullWidth
                  size="large"
                >
                  Create Flow
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => setExportOpen(true)}
                  fullWidth
                  size="large"
                >
                  Export Data
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <BroadcastDialog
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        onComplete={handleBroadcastComplete}
      />
      
      <CreateFlowDialog
        open={flowOpen}
        onClose={() => setFlowOpen(false)}
        onComplete={handleFlowComplete}
      />
      
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onComplete={handleExportComplete}
      />

      {/* Add SMB Dialog - Only for agency users */}
      {user?.role === 'agency' && (
        <AddSMBDialog
          open={addSMBOpen}
          onClose={() => setAddSMBOpen(false)}
          onSuccess={handleSMBAddSuccess}
        />
      )}
    </>
  )
}

export default QuickActions
